// Clarkson University: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Clarkson posts no Common Data Set on clarkson.edu (no Institutional
//    Research or CDS page in the site's sitemap, its consumer information
//    page or its site search). The first-year counts come from the U.S.
//    Department of Education's IPEDS Admissions survey for Fall 2024,
//    first-time degree-seeking students (College Navigator and the ADM2024
//    complete data file, unit 190044).
//  - Clarkson Admissions: Apply to Clarkson (first-year deadlines), How to
//    Apply as an Incoming Undergraduate (materials and test policy)
//    and the Undergraduate Admissions FAQs. These pages carry no
//    cycle year; they are the pages as posted on 2026-10-01, when the
//    application for fall 2027 entry is open. Clarkson does not publish
//    decision dates, and its pages do not spell out the early decision
//    commitment.
//  - Clarkson Undergraduate Tuition & Costs, Cost of Attendance 2026-2027
//    (U.S. citizens and permanent residents; housing is a weighted average).
//  - College Scorecard, school id 190044 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=190044#admsns',
        read: '2026-10-01',
    },
    apply: {
        label: 'Clarkson University, Apply to Clarkson',
        url: 'https://www.clarkson.edu/apply',
        read: '2026-10-01',
    },
    howToApply: {
        label: 'Clarkson Admissions, How to Apply as an Incoming Undergraduate',
        url: 'https://www.clarkson.edu/admissions-aid/undergraduate/how-to-apply',
        read: '2026-10-01',
    },
    faqs: {
        label: 'Clarkson Admissions, Undergraduate Admissions FAQs',
        url: 'https://www.clarkson.edu/admissions-aid/undergraduate/undergraduate-admissions-faqs',
        read: '2026-10-01',
    },
    costs: {
        label: 'Clarkson University, Undergraduate Tuition & Costs',
        url: 'https://www.clarkson.edu/admissions-aid/undergraduate/tuition-costs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?190044-Clarkson-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'clarkson-university',
    kbId: 'clarkson_university',
    name: 'Clarkson University',
    shortName: 'Clarkson',
    location: 'Potsdam, NY',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Clarkson brand page); decoration only.
    branding: {
        primary: '#004E42',
        secondary: '#FFCD00',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/clarkson_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 6661,
        admitted: 5153,
        enrolled: 489,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Clarkson publishes no Common Data Set and no first-year acceptance rate by school or major; the federal IPEDS counts give one rate for the whole university.',
        source: 'ipeds',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision', deadline: 'December 1', decision: null, binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: null, binding: false },
        ],
        note: 'Clarkson does not publish decision dates. File the FAFSA by February 1 for need-based aid. There is no application fee for first-year applicants who use the Clarkson application or the Common App. Clarkson\'s pages give the early decision deadline but do not spell out its terms; read the early decision agreement before you sign it.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Clarkson is test-optional. If you send SAT or ACT scores, they are taken into consideration, and Clarkson accepts scores you report yourself. Every applicant sends an official high school transcript, a letter from a school counselor, principal or headmaster, a second letter from a core subject teacher, and a personal statement of 250 to 650 words.',
        source: 'howToApply',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'faqs',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 61694 },
            { item: 'Fees', amount: 1370 },
            { item: 'Housing (average)', amount: 10840 },
            { item: 'Food', amount: 8528 },
            { item: 'Books, course materials, supplies and equipment', amount: 1560 },
            { item: 'Personal', amount: 900 },
            { item: 'Transportation', amount: 1596 },
            { item: 'Loan fees', amount: 68 },
        ],
        total: 86556,
        note: 'For U.S. citizens and permanent residents. Housing is a weighted average; Clarkson says first-year housing usually costs slightly less.',
        source: 'costs',
        netPrice: {
            average: 32137,
            byIncome: [
                { income: '$0 to $30,000', amount: 18064 },
                { income: '$30,001 to $48,000', amount: 19353 },
                { income: '$48,001 to $75,000', amount: 24424 },
                { income: '$75,001 to $110,000', amount: 30286 },
                { income: 'Over $110,000', amount: 37156 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Clarkson\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
